import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, MessageCircle, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { MovingBG } from './MovingBG';
import { FuturisticBG } from './FuturisticBG';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PROJECT_TYPES = [
  'Music Video',
  'Short Film',
  'Event Coverage',
  'Automotive Showcase',
  'Wedding',
  'Commercial / Advertisement',
  'Corporate Video',
  'Photography Session',
  'Other',
];

const WhatsAppIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export default function ShootWithUs() {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    brand: '',
    project_type: '',
    location: '',
    timeline: '',
  });

  // Generate unique event ID for deduplication
  const generateEventId = () => `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // Safe fbq call with retry
  const trackEvent = (eventName, params = {}) => {
    const eventId = generateEventId();
    const trackWithRetry = (attempt = 1) => {
      if (typeof window.fbq === 'function') {
        try {
          window.fbq('track', eventName, { ...params, eventID: eventId });
          console.log(`[Meta Pixel] ${eventName} tracked successfully`, { eventId, params });
        } catch (err) {
          console.error(`[Meta Pixel] Error tracking ${eventName}:`, err);
        }
      } else if (attempt < 5) {
        // Retry after 500ms if fbq not ready yet
        setTimeout(() => trackWithRetry(attempt + 1), 500);
      } else {
        console.warn(`[Meta Pixel] fbq not available after ${attempt} attempts`);
      }
    };
    trackWithRetry();
  };

  // Track Contact/Chat when WhatsApp button is clicked
  const handleWhatsAppClick = () => {
    trackEvent('Contact', { 
      content_name: 'WhatsApp Chat',
      content_category: 'Inquiry'
    });
  };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const allFilled = Object.values(form).every((v) => v.trim() !== '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allFilled) return;
    setLoading(true);
    
    // Track Lead event IMMEDIATELY when form is submitted (before API call)
    // This ensures we capture the lead even if API fails
    trackEvent('Lead', { 
      content_name: form.project_type,
      content_category: 'Inquiry Form',
      value: 1,
      currency: 'MYR'
    });
    
    try {
      const res = await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        // Still show success to user even if API had issues
        setSubmitted(true);
      }
    } catch {
      // still show success if stored locally
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  const whatsappUrl =
    'https://wa.me/601133131844?text=Hello%20TAF%20Worldwide%2C%20I%20just%20submitted%20the%20project%20inquiry%20form%20and%20would%20like%20to%20discuss%20my%20project%20further.';

  return (
    <div className="min-h-screen relative" style={{ background: '#050505' }}>
      <MovingBG />
      <FuturisticBG density={8} />

      {/* Back button */}
      <div className="fixed top-6 left-6 z-50">
        <button
          onClick={() => navigate('/')}
          data-testid="back-to-home-btn"
          className="flex items-center gap-2 text-sm text-gray-400 hover:text-orange-400 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-20">
        <div className="w-full max-w-lg">
          {!submitted ? (
            <>
              {/* Header */}
              <div className="text-center mb-10">
                <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-orange-400 mb-4">
                  TAF WORLDWIDE
                </p>
                <h1
                  className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4 leading-tight"
                  style={{ fontFamily: "'TafHeadline'", letterSpacing: '0.04em' }}
                  data-testid="inquiry-heading"
                >
                  Start Your Project
                </h1>
                <p className="text-gray-400 text-sm sm:text-base max-w-md mx-auto">
                  Share a few details about your project and our team will get in touch.
                </p>
                <div className="w-16 h-0.5 bg-gradient-to-r from-orange-500 to-amber-500 mx-auto mt-5" />
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-orange-400/80 mb-2">
                    Name
                  </label>
                  <input
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    data-testid="inquiry-name"
                    placeholder="Your full name"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all text-sm"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-orange-400/80 mb-2">
                    Email
                  </label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    data-testid="inquiry-email"
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all text-sm"
                  />
                </div>

                {/* Phone (WhatsApp) */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-orange-400/80 mb-2">
                    Phone Number (WhatsApp)
                  </label>
                  <input
                    name="phone"
                    type="tel"
                    value={form.phone}
                    onChange={handleChange}
                    required
                    data-testid="inquiry-phone"
                    placeholder="e.g. +60 12-345 6789"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all text-sm"
                  />
                </div>

                {/* Brand / Company / Event / Ride */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-orange-400/80 mb-2">
                    Brand / Company / Event / Ride
                  </label>
                  <input
                    name="brand"
                    value={form.brand}
                    onChange={handleChange}
                    required
                    data-testid="inquiry-brand"
                    placeholder="e.g. Nike, BMW Club MY, Wedding Reception"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all text-sm"
                  />
                </div>

                {/* Type of Project */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-orange-400/80 mb-2">
                    Type of Project
                  </label>
                  <select
                    name="project_type"
                    value={form.project_type}
                    onChange={handleChange}
                    required
                    data-testid="inquiry-project-type"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all text-sm appearance-none cursor-pointer"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23f97316' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="" disabled className="bg-gray-900 text-gray-500">
                      Select project type
                    </option>
                    {PROJECT_TYPES.map((t) => (
                      <option key={t} value={t} className="bg-gray-900 text-white">
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-orange-400/80 mb-2">
                    Location
                  </label>
                  <input
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    required
                    data-testid="inquiry-location"
                    placeholder="e.g. Kuala Lumpur, Johor Bahru"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all text-sm"
                  />
                </div>

                {/* Date / Timeline */}
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-orange-400/80 mb-2">
                    Date / Timeline
                  </label>
                  <input
                    name="timeline"
                    value={form.timeline}
                    onChange={handleChange}
                    required
                    data-testid="inquiry-timeline"
                    placeholder="e.g. March 2026, ASAP, Flexible"
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-600 focus:outline-none focus:border-orange-500/60 focus:bg-white/[0.07] transition-all text-sm"
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={!allFilled || loading}
                  data-testid="inquiry-submit-btn"
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-500 hover:to-amber-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold rounded-xl transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl hover:shadow-orange-500/25 text-base tracking-wide mt-8"
                >
                  {loading ? (
                    <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Submit Inquiry
                    </>
                  )}
                </button>
              </form>
            </>
          ) : (
            /* ── Success State ── */
            <div className="text-center py-8">
              <div className="w-20 h-20 bg-orange-500/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle2 className="w-10 h-10 text-orange-400" />
              </div>
              <h2
                className="text-2xl sm:text-3xl font-black text-white mb-4"
                style={{ fontFamily: "'TafHeadline'", letterSpacing: '0.04em' }}
                data-testid="inquiry-success-heading"
              >
                Thank you.
              </h2>
              <p className="text-gray-400 mb-3 text-sm sm:text-base">
                Your inquiry has been received by TAF Worldwide.
              </p>
              <p className="text-gray-500 text-sm mb-10">
                For faster response, you can message our team directly on WhatsApp.
              </p>

              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick}
                data-testid="inquiry-whatsapp-btn"
                className="inline-flex items-center gap-3 px-10 py-5 text-white font-bold text-lg rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-green-500/25"
                style={{ background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}
              >
                <WhatsAppIcon />
                Message Us on WhatsApp
              </a>

              <button
                onClick={() => navigate('/')}
                data-testid="back-home-after-submit"
                className="block mx-auto mt-8 text-sm text-gray-500 hover:text-orange-400 transition-colors"
              >
                Back to Home
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
